import {
  isDouyinMiniProgramEnv,
  isLfMiniProgramEnv,
  loadMiniProgramBridge,
} from "./miniProgramEnv";
import { resolveShellBgFromPathname } from "./pageShellBg";

export const MINI_PROGRAM_NAV_BAR_MSG = "lfNavBar" as const;

export type MiniProgramNavBarPayload = {
  type: typeof MINI_PROGRAM_NAV_BAR_MSG;
  backgroundColor: string;
  frontColor: "#ffffff" | "#000000";
  pathname: string;
};

type NavBarTheme = Pick<MiniProgramNavBarPayload, "backgroundColor" | "frontColor">;

/** tt.setNavigationBarColor 要求完整 6 位 hex */
function toNavBarHex(color: string): string {
  const trimmed = color.trim();
  const rgb = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const hex = (n: string) => Number(n).toString(16).padStart(2, "0");
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
  }
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return trimmed;
}

function resolveNavBarTheme(pathname: string): NavBarTheme {
  const { bg, front } = resolveShellBgFromPathname(pathname);
  return {
    backgroundColor: toNavBarHex(bg),
    frontColor: front,
  };
}

/** 部分宿主在 web-view 内注入了完整 tt/wx，可直接改状态栏前景色 */
function tryDirectSetNavigationBarColor(theme: NavBarTheme): void {
  const w = window as Window & {
    tt?: { setNavigationBarColor?: (opts: NavBarTheme) => void };
    wx?: { setNavigationBarColor?: (opts: NavBarTheme) => void };
  };
  const api = isDouyinMiniProgramEnv() ? w.tt : w.wx;
  try {
    api?.setNavigationBarColor?.(theme);
  } catch {
    /* web-view 内可能不可用 */
  }
}

/**
 * H5 路由变化时同步小程序顶栏/状态栏颜色。
 * 白底页需 frontColor=#000000，否则系统状态栏图标仍为白色。
 * 主路径靠路由后 syncMiniProgramNavBar；此处作直调 / postMessage 兜底。
 * 空间内 Tab 不重载 web-view。
 */
export function syncMiniProgramNavBar(pathname: string): void {
  if (!isLfMiniProgramEnv()) {
    return;
  }
  const theme = resolveNavBarTheme(pathname);
  const payload: MiniProgramNavBarPayload = {
    type: MINI_PROGRAM_NAV_BAR_MSG,
    pathname,
    ...theme,
  };

  tryDirectSetNavigationBarColor(theme);

  void loadMiniProgramBridge().then((bridge) => {
    const mp = bridge?.miniProgram as
      | { postMessage?: (opts: { data: MiniProgramNavBarPayload }) => void }
      | undefined;
    mp?.postMessage?.({ data: payload });
  });
}
